import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import TextOverflow from "~/components/TextOverflow";
import { deleteDataset } from "~/services/catalog";
import { getApiErrorMessage, isApiError } from "~/utils/apiClient";
import rumClient, { trackEvent } from "~/utils/monitoring";

const DeleteDatasetModal = ({
  id,
  name,
  onClose,
  open,
}: {
  id: string;
  name: string;
  onClose: () => void;
  open: boolean;
}) => {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      await deleteDataset(id);

      trackEvent("dataset_deleted", { dataset_id: id });
      navigate("/data-browser/datasets");
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      setError(`Unable to archive dataset${isApiError(e) ? `: ${getApiErrorMessage(e)}` : ""}`);
    }

    setSubmitting(false);
  };

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <TextOverflow>{`Archive "${name}"`}</TextOverflow>
        </DialogTitle>
        <DialogContent sx={{ gap: 2, fontSize: 14 }}>
          {error && <Alert severity="error">{error}</Alert>}
          Archive and hide this dataset. An admin can restore if necessary.
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={submitting}
            sx={{
              backgroundColor: "attention.main",
              "&:hover": { backgroundColor: "attention.dark" },
            }}
          >
            Archive
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DeleteDatasetModal;
